import React from 'react';
import { LanguageAdminDto } from '../../types';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react'; 

interface LanguageFormData {
    code: string;
    name: string;
    nativeName: string;
    flagEmoji: string;
    isActive: boolean;
    sortOrder: number;
    translationKey: string;
}

interface SetupPageProps {
    isOpen: boolean;
    onClose: () => void;
    isCreating: boolean;
    selectedLanguage: LanguageAdminDto | null;
    formData: LanguageFormData;
    setFormData: React.Dispatch<React.SetStateAction<LanguageFormData>>;
    handleSave: () => void;
}

const SetupPage: React.FC<SetupPageProps> = ({
    isOpen,
    onClose,
    isCreating,
    formData,
    setFormData,
    handleSave,
}) => {
    if (!isOpen) return null;

    const handleEmojiClick = (emojiData: EmojiClickData) => {
        // Split the unified string (handles composite emojis like flags which use hyphen separation)
        const emojiString = emojiData.unified
            .split('-')
            .map(hex => String.fromCodePoint(parseInt(hex, 16)))
            .join('');

        setFormData({ ...formData, flagEmoji: emojiString });
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-bold">
                        {isCreating ? 'Create Language' : 'Edit Language'}
                    </h2>
                    <button onClick={onClose}>✕</button>
                </div>

                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">Code</label>
                        <input
                            className="w-full border p-2 rounded"
                            value={formData.code}
                            onChange={e => setFormData({ ...formData, code: e.target.value })}
                            disabled={!isCreating}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Name</label>
                        <input
                            className="w-full border p-2 rounded"
                            value={formData.name}
                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Translation Key</label>
                        <input
                            className="w-full border p-2 rounded"
                            placeholder="be-nl"
                            value={formData.translationKey || ''}
                            onChange={e => setFormData({ ...formData, translationKey: e.target.value })}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            The key used to load translations (e.g. "be-nl", "en")
                        </p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Native Name</label>
                        <input
                            className="w-full border p-2 rounded"
                            value={formData.nativeName}
                            onChange={e => setFormData({ ...formData, nativeName: e.target.value })}
                        />
                    </div>

                    {/* Flag Emoji with Picker */}
                    <div>
                        <label className="block text-sm font-medium mb-1">
                            Flag Emoji
                        </label>
                        <EmojiPicker onEmojiClick={handleEmojiClick} />
                        <div className="mt-2 flex items-center gap-2">
                            <span>Selected:</span>
                            <span className="text-2xl">{formData.flagEmoji || 'None'}</span>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={formData.isActive}
                            onChange={e => setFormData({ ...formData, isActive: e.target.checked })}
                        />
                        <label>Active</label>
                    </div>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                    <button onClick={onClose} className="px-4 py-2 border rounded">
                        Cancel
                    </button>
                    <button onClick={handleSave} className="px-4 py-2 bg-blue-600 text-white rounded">
                        {isCreating ? 'Create' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SetupPage;